import { useEffect, useState, useRef } from "react";
import { Loader2, Upload, Plus, X } from "lucide-react";
import { fetchWithAuth } from "../api/fetchWithAuth";
import PlatformCoverageAoiMap from "./components/PlatformCoverageAoiMap";

export default function CoverageAoisPanel() {
    const [aois, setAois] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedId, setSelectedId] = useState(null);

    // Draft AOI state — new draw flow
    const [draftMode, setDraftMode] = useState(false);
    const [draftName, setDraftName] = useState("");
    const [draftBasin, setDraftBasin] = useState("");
    const [draftRegion, setDraftRegion] = useState("");
    const [draftReady, setDraftReady] = useState(false);
    const [draftSaving, setDraftSaving] = useState(false);
    const [draftError, setDraftError] = useState(null);
    const [saveRequestCount, setSaveRequestCount] = useState(0);

    // Upload state
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadName, setUploadName] = useState("");
    const [uploadBasin, setUploadBasin] = useState("");
    const [uploadRegion, setUploadRegion] = useState("");
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const fileInputRef = useRef(null);

    async function loadAois() {
        setLoading(true);
        try {
            const res = await fetchWithAuth("/api/v1/platform/coverage-aois");
            if (!res.ok) throw new Error("Failed to load AOIs");
            const data = await res.json();
            setAois(data.coverage_aois || []);
        } catch (e) {
            setError("Failed to load coverage AOIs");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { loadAois(); }, []);

    function startNewAoi() {
        setDraftMode(true);
        setDraftReady(false);
        setDraftName("");
        setDraftBasin("");
        setDraftRegion("");
        setDraftError(null);
        setSaveRequestCount(0);
        setSelectedId(null);
    }

    function cancelDraft() {
        setDraftMode(false);
        setDraftReady(false);
        setDraftName("");
        setDraftBasin("");
        setDraftRegion("");
        setDraftError(null);
        setDraftSaving(false);
        setSaveRequestCount(0);
    }

    function onPolygonComplete() {
        setDraftReady(true);
    }

    function onDrawReset() {
        setDraftReady(false);
        setDraftSaving(false);
    }

    function handleSaveClick() {
        if (!draftName.trim()) {
            setDraftError("Name is required");
            return;
        }
        setDraftSaving(true);
        setDraftError(null);
        setSaveRequestCount(c => c + 1);
    }

    function handleFileSelected(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadFile(file);
        setUploadName(file.name.replace(/\.[^.]+$/, ""));
        setUploadError(null);
        e.target.value = "";
    }

    async function submitUpload() {
        if (!uploadFile) return;
        setUploading(true);
        setUploadError(null);
        try {
            const formData = new FormData();
            formData.append("file", uploadFile);
            formData.append("name", uploadName || uploadFile.name.replace(/\.[^.]+$/, ""));
            if (uploadBasin) formData.append("basin", uploadBasin);
            if (uploadRegion) formData.append("region", uploadRegion);

            const res = await fetchWithAuth("/api/v1/platform/coverage-aois/import", {
                method: "POST",
                body: formData,
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || data.error || "Import failed");

            setUploadFile(null);
            setUploadName("");
            setUploadBasin("");
            setUploadRegion("");
            await loadAois();
            setSelectedId(data.id);
        } catch (e) {
            setUploadError(e.message);
        } finally {
            setUploading(false);
        }
    }

    return (
        <div className="flex h-full">
            {/* LEFT COLUMN */}
            <div className="w-80 shrink-0 border-r border-bg-border bg-bg-soft p-4 flex flex-col">

                {/* HEADER */}
                <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold">Entitlement AOIs</h2>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            className="flex items-center gap-1 px-2 py-1 rounded border border-bg-border
                                       text-gray-400 hover:text-gray-200 hover:border-gray-500 text-xs"
                            title="Import file"
                            disabled={draftMode}
                        >
                            <Upload size={12} />
                            Import
                        </button>
                        {!draftMode && (
                            <button
                                onClick={startNewAoi}
                                className="flex items-center gap-1 px-2 py-1 rounded
                                           border border-accent-action/60 text-accent-action
                                           hover:bg-accent-action/20 text-xs"
                            >
                                <Plus size={12} />
                                New AOI
                            </button>
                        )}
                    </div>
                </div>

                <p className="mt-2 text-xs text-gray-400">
                    Platform-managed coverage areas used for AOI-scoped entitlements.
                </p>

                {/* Hidden file input */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".geojson,.json,.kml,.kmz,.zip"
                    className="hidden"
                    onChange={handleFileSelected}
                />

                {/* Upload form */}
                {uploadFile && (
                    <div className="mt-3 p-3 bg-bg border border-bg-border rounded space-y-2">
                        <p className="text-xs text-gray-400 truncate">📄 {uploadFile.name}</p>
                        <input
                            type="text"
                            placeholder="Name"
                            value={uploadName}
                            onChange={e => setUploadName(e.target.value)}
                            className="w-full bg-bg-soft border border-bg-border rounded px-2 py-1 text-xs"
                        />
                        <div className="flex gap-2">
                            <input
                                type="text"
                                placeholder="Basin (optional)"
                                value={uploadBasin}
                                onChange={e => setUploadBasin(e.target.value)}
                                className="flex-1 bg-bg-soft border border-bg-border rounded px-2 py-1 text-xs"
                            />
                            <input
                                type="text"
                                placeholder="Region (optional)"
                                value={uploadRegion}
                                onChange={e => setUploadRegion(e.target.value)}
                                className="flex-1 bg-bg-soft border border-bg-border rounded px-2 py-1 text-xs"
                            />
                        </div>
                        {uploadError && (
                            <p className="text-xs text-red-400">{uploadError}</p>
                        )}
                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={() => { setUploadFile(null); setUploadError(null); }}
                                className="px-2 py-1 text-xs text-gray-400 border border-bg-border rounded"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={submitUpload}
                                disabled={uploading || !uploadName}
                                className="px-2 py-1 text-xs bg-accent-action/20 border border-accent-action/60
                                           text-accent-action rounded disabled:opacity-50"
                            >
                                {uploading ? "Importing…" : "Import"}
                            </button>
                        </div>
                    </div>
                )}

                {/* AOI LIST */}
                <div className="mt-4 flex-1 overflow-auto space-y-0.5">

                    {/* DRAFT ENTRY */}
                    {draftMode && (
                        <div className="rounded border border-aoi-neon/40 bg-aoi-neon/5 p-2 space-y-2">
                            <div className="flex items-center justify-between">
                                <span className="text-[11px] text-aoi-neon font-medium">
                                    {draftReady ? "Name your AOI" : "Draw AOI on map…"}
                                </span>
                                <button
                                    onClick={cancelDraft}
                                    className="text-fg-secondary hover:text-gray-300"
                                >
                                    <X size={12} />
                                </button>
                            </div>

                            <input
                                type="text"
                                placeholder="AOI Name *"
                                value={draftName}
                                onChange={e => setDraftName(e.target.value)}
                                disabled={!draftReady}
                                autoFocus={draftReady}
                                className="w-full bg-bg border border-bg-border rounded px-2 py-1
                                           text-xs text-gray-200 placeholder-gray-600
                                           disabled:opacity-40 disabled:cursor-not-allowed
                                           focus:outline-none focus:border-aoi-neon"
                            />
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    placeholder="Basin"
                                    value={draftBasin}
                                    onChange={e => setDraftBasin(e.target.value)}
                                    disabled={!draftReady}
                                    className="flex-1 bg-bg border border-bg-border rounded px-2 py-1
                                               text-xs text-gray-200 placeholder-gray-600
                                               disabled:opacity-40 disabled:cursor-not-allowed
                                               focus:outline-none focus:border-aoi-neon"
                                />
                                <input
                                    type="text"
                                    placeholder="Region"
                                    value={draftRegion}
                                    onChange={e => setDraftRegion(e.target.value)}
                                    disabled={!draftReady}
                                    className="flex-1 bg-bg border border-bg-border rounded px-2 py-1
                                               text-xs text-gray-200 placeholder-gray-600
                                               disabled:opacity-40 disabled:cursor-not-allowed
                                               focus:outline-none focus:border-aoi-neon"
                                />
                            </div>

                            {draftError && (
                                <p className="text-[11px] text-red-400">{draftError}</p>
                            )}

                            {draftReady && (
                                <button
                                    onClick={handleSaveClick}
                                    disabled={draftSaving || !draftName.trim()}
                                    className="w-full py-1 rounded text-xs
                                               bg-aoi-neon/20 border border-aoi-neon/60 text-aoi-neon
                                               disabled:opacity-40 hover:bg-aoi-neon/30 transition"
                                >
                                    {draftSaving ? "Saving…" : "Save AOI"}
                                </button>
                            )}
                        </div>
                    )}

                    {/* EXISTING AOIs */}
                    {loading && (
                        <div className="flex justify-center pt-6">
                            <Loader2 className="animate-spin text-gray-400" />
                        </div>
                    )}
                    {error && (
                        <div className="pt-4 text-sm text-red-400">{error}</div>
                    )}
                    {!loading && !error && aois.length === 0 && !draftMode && (
                        <div className="pt-4 text-sm text-fg-secondary">
                            No entitlement AOIs defined yet.
                        </div>
                    )}
                    {!loading && !error && aois.map(aoi => (
                        <button
                            key={aoi.id}
                            onClick={() => { setSelectedId(aoi.id); cancelDraft(); }}
                            className={`w-full text-left px-1 py-1 rounded border border-transparent
                                ${selectedId === aoi.id
                                    ? "bg-accent-red/20 border-accent-red/40 text-orange-300"
                                    : "hover:bg-bg text-gray-300"}`}
                        >
                            <div className="text-[11px] truncate">{aoi.name}</div>
                            <div className="flex items-center gap-2 text-xs text-white/60">
                                <span>{aoi.area_sqmi?.toLocaleString()} sq mi</span>
                                {aoi.area_sqmi > 1000 && (
                                    <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold
                                                     bg-orange-500/15 text-orange-300 border border-orange-500/30">
                                        Large AOI
                                    </span>
                                )}
                            </div>
                            <div className="text-[11px] text-fg-secondary mt-1 flex gap-2">
                                {aoi.basin && <span>{aoi.basin}</span>}
                                {aoi.region && <span>• {aoi.region}</span>}
                                {aoi.immutable && (
                                    <span className="text-orange-400">• immutable</span>
                                )}
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {/* MAP */}
            <div className="flex-1 bg-black">
                <PlatformCoverageAoiMap
                    aois={aois}
                    selectedId={selectedId}
                    createMode={draftMode}
                    meta={{
                        name: draftName,
                        basin: draftBasin,
                        region: draftRegion,
                        slug: draftName.toLowerCase().replace(/\s+/g, "-"),
                    }}
                    onPolygonComplete={onPolygonComplete}
                    onDrawReset={onDrawReset}
                    onSaveRequested={saveRequestCount}
                    onSaveDone={() => setDraftSaving(false)}
                    onSaveError={(msg) => {
                        setDraftError(msg);
                        setDraftSaving(false);
                    }}
                    onCreated={async (newId) => {
                        setDraftMode(false);
                        setDraftReady(false);
                        setDraftName("");
                        setDraftBasin("");
                        setDraftRegion("");
                        setDraftSaving(false);
                        setSaveRequestCount(0);
                        await loadAois();
                        if (newId) setSelectedId(newId);
                    }}
                />
            </div>
        </div>
    );
}